import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { PaymentMethod } from 'src/models/payment-methods/_types';
import {
  fetchAccessiblePaymentMethods,
  fetchAccessiblePaymentMethodsQueryKey,
} from 'src/models/payment-methods/payment-methods';
import { getPaymentMethodIcon } from 'src/models/payment-methods/utils';
import { User } from 'src/models/user/_types';
import { Identifier } from 'src/types/utilityTypes';
import { capitalizeFirstLetter } from 'src/utils/stringFormatter';

interface PaymentMethodsDialog {
  open: boolean;
  onClose: () => void;
  user: User | null;
  setPaymentMethod: (paymentMethod: PaymentMethod | null) => void;
}

export default function PaymentMethodsDialog({
  open,
  onClose,
  user,
  setPaymentMethod,
}: PaymentMethodsDialog) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pointOfSale.paymentMethods',
  });

  const { data: accessiblePaymentMethods, isLoading } = useQuery({
    queryKey: fetchAccessiblePaymentMethodsQueryKey(user?.id),
    queryFn: () => fetchAccessiblePaymentMethods(user?.id as Identifier),
    enabled: Boolean(user?.id),
  });

  useEffect(() => {
    if (accessiblePaymentMethods && !isLoading) {
      const defaultPaymentMethod = accessiblePaymentMethods.find(
        (method: PaymentMethod) => method.default
      );
      setPaymentMethod(defaultPaymentMethod || null);
    }
  }, [accessiblePaymentMethods, isLoading, setPaymentMethod]);

  const handlePaymentMethodClick = (method: PaymentMethod | null) => {
    setPaymentMethod(method);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('title')}>
      <Stack>
        {accessiblePaymentMethods?.map((method) => (
          <ListItem
            key={method.id}
            primary={
              <Box display="flex" alignItems="center">
                <Icon
                  size="medium"
                  icon={getPaymentMethodIcon(method.paymentMethodType)}
                  sx={{ mr: 3, ml: 1 }}
                />
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {t('brandEndingIn', {
                      brand: capitalizeFirstLetter(method.brand),
                      last4: method.last4,
                    })}
                  </Typography>
                  {method.expMonth && method.expYear && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {t('expiresOn', {
                        month: method.expMonth,
                        year: method.expYear,
                      })}
                    </Typography>
                  )}
                  {method.default && (
                    <Typography variant="body2" color="primary">
                      {t('defaultPaymentMethod')}
                    </Typography>
                  )}
                </Box>
              </Box>
            }
            onClick={() => handlePaymentMethodClick(method)}
          />
        ))}
        <ListItem
          primary={
            <Box display="flex" alignItems="center">
              <Icon
                size="medium"
                icon={getPaymentMethodIcon('card')}
                sx={{ mr: 3, ml: 1 }}
              />
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('swipeCard')}
                </Typography>
              </Box>
            </Box>
          }
          onClick={() => handlePaymentMethodClick(null)}
        />
      </Stack>
    </Dialog>
  );
}
